import React, { useCallback, useEffect, useState } from 'react';
import { Paperclip } from 'lucide-react';
import { useAdminApi, errorMessage } from './adminApi';
import { useQueryParams } from './useQueryParams';
import { formatBytes, formatDate, formatDateTime } from './format';
import {
  Badge, DataTable, Drawer, ListLayout, Pagination, Panel, SearchInput, Spinner, Toolbar, btnSecondary
} from './AdminUI';

// The formatted version renders in a sandboxed frame: no scripts, and nothing loads from outside.
const HTML_POLICY = '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; style-src \'unsafe-inline\'; img-src data:">';

const SKIPPED_LABELS = { too_large: 'Too large, kept in Gmail', no_data: 'Not kept' };

function AttachmentList({ condo, email }) {
  const api = useAdminApi();
  const [busy, setBusy] = useState(null);

  const download = async (attachment) => {
    setBusy(attachment.index);
    try {
      const response = await api.download(`/api/admin/condos/${condo._id}/emails/${email._id}/attachments/${attachment.index}`);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err?.response?.status === 404 ? 'This file is not on this server.' : 'Failed to download the attachment');
    } finally {
      setBusy(null);
    }
  };

  if (!email.attachments.length) return null;
  return (
    <section className="mt-6">
      <h3 className="text-sm font-semibold text-foreground mb-2">Attachments ({email.attachments.length})</h3>
      <ul className="space-y-2">
        {email.attachments.map((attachment) => (
          <li key={attachment.index} className="flex items-center gap-3 p-2 rounded border border-border text-sm">
            <Paperclip className="h-4 w-4 text-muted-foreground flex-none" />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-foreground break-all">{attachment.name}</p>
              <p className="text-xs text-muted-foreground">{formatBytes(attachment.size)}{!attachment.stored && ` · ${SKIPPED_LABELS[attachment.skipped] || 'Not kept'}`}</p>
            </div>
            {attachment.stored && (
              <button type="button" onClick={() => download(attachment)} disabled={busy === attachment.index} className={btnSecondary}>
                {busy === attachment.index ? 'Downloading...' : 'Download'}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function EmailDetail({ condo, emailId, onClose }) {
  const api = useAdminApi();
  const [data, setData] = useState(null);
  const [showHtml, setShowHtml] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/api/admin/condos/${condo._id}/emails/${emailId}`)
      .then((response) => { if (!cancelled) setData(response.data); })
      .catch((err) => { if (!cancelled) setData({ error: errorMessage(err, 'Failed to load email') }); });
    return () => { cancelled = true; };
  }, [api, condo._id, emailId]);

  if (!data) return <Drawer title="Email" onClose={onClose}><Spinner /></Drawer>;
  if (data.error) return <Drawer title="Email" onClose={onClose}><p className="text-sm text-destructive">{data.error}</p></Drawer>;

  const { email, thread } = data;
  const headers = [
    ['From', email.from],
    ['To', (email.to || []).join(', ')],
    ['Cc', (email.cc || []).join(', ')],
    ['Received', formatDateTime(email.receivedAt)],
    ['Thread', thread ? `${thread.title} (${thread.emailCount} email${thread.emailCount === 1 ? '' : 's'})` : '']
  ].filter(([, value]) => value);

  return (
    <Drawer title={email.subject || '(no subject)'} onClose={onClose}>
      <dl className="space-y-1 text-sm">
        {headers.map(([label, value]) => (
          <div key={label} className="flex gap-3">
            <dt className="w-24 flex-none text-muted-foreground">{label}</dt>
            <dd className="min-w-0 text-foreground break-words">{value}</dd>
          </div>
        ))}
      </dl>
      <section className="mt-6">
        <h3 className="text-sm font-semibold text-foreground mb-1">Summary</h3>
        <p className="text-xs text-muted-foreground mb-2">What the agents read first.</p>
        <p className="text-sm text-foreground whitespace-pre-wrap">{email.summary || '—'}</p>
      </section>
      <section className="mt-6">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-sm font-semibold text-foreground mr-auto">Full email</h3>
          {email.bodyHtml && (
            <button type="button" onClick={() => setShowHtml(!showHtml)} className={btnSecondary}>
              {showHtml ? 'Show plain text' : 'Show formatted'}
            </button>
          )}
        </div>
        {!email.hasFullRecord ? (
          <p className="text-sm text-muted-foreground">Only the summary was kept for this email: it was archived before full emails were kept.</p>
        ) : showHtml ? (
          <iframe title="Formatted email" sandbox="" srcDoc={HTML_POLICY + email.bodyHtml} className="w-full h-96 rounded border border-border bg-white" />
        ) : (
          <pre className="text-sm text-foreground whitespace-pre-wrap break-words font-sans bg-background rounded border border-border p-3 max-h-96 overflow-y-auto">{email.body}</pre>
        )}
        {email.bodyTruncated && <p className="text-xs text-muted-foreground mt-2">This email was very long and was cut off when it was saved. The original is in Gmail.</p>}
      </section>
      <AttachmentList condo={condo} email={email} />
    </Drawer>
  );
}

function EmailsTab({ condo }) {
  const api = useAdminApi();
  const [params, setParams] = useQueryParams();
  const [data, setData] = useState(null);

  const q = params.get('q') || '';
  const pageParam = Number(params.get('page')) || null;
  const selectedId = params.get('email');

  const load = useCallback(async () => {
    try {
      const response = await api.get(`/api/admin/condos/${condo._id}/emails`, { q: q || undefined, page: pageParam || undefined, focus: pageParam ? undefined : selectedId || undefined });
      setData(response.data);
    } catch (err) {
      alert(errorMessage(err, 'Failed to fetch emails'));
    }
  }, [api, condo._id, q, pageParam, selectedId]);

  useEffect(() => { load(); }, [load]);

  // With an email open the list is narrow, so the sender moves under the subject.
  const compact = Boolean(selectedId);
  const columns = [
    { key: 'receivedAt', header: 'Received', className: 'whitespace-nowrap text-muted-foreground', render: (e) => (compact ? formatDate(e.receivedAt) : formatDateTime(e.receivedAt)) },
    ...(compact ? [] : [{ key: 'from', header: 'From', hideBelow: 'md', className: 'break-all' }]),
    {
      key: 'subject',
      header: 'Subject',
      render: (e) => (
        <div>
          <p className="font-medium text-foreground">{e.subject || '(no subject)'}</p>
          {compact && <p className="text-xs text-muted-foreground break-all">{e.from}</p>}
          {!compact && e.threadTitle && e.threadTitle !== e.subject && <p className="text-xs text-muted-foreground">{e.threadTitle}</p>}
        </div>
      )
    },
    {
      key: 'attachments',
      header: 'Files',
      hideBelow: compact ? 'xl' : 'lg',
      render: (e) => (e.attachments.length ? <span className="inline-flex items-center gap-1 text-muted-foreground"><Paperclip className="h-3 w-3" />{e.attachments.length}</span> : '—')
    },
    ...(compact ? [] : [{ key: 'record', header: 'Record', hideBelow: 'lg', render: (e) => (e.hasFullRecord ? <Badge>Full</Badge> : <Badge>Summary only</Badge>) }])
  ];

  const drawer = selectedId ? (
    <EmailDetail key={selectedId} condo={condo} emailId={selectedId} onClose={() => setParams({ email: null })} />
  ) : null;

  return (
    <ListLayout drawer={drawer} wideDrawer>
      <Panel>
        <Toolbar>
          <h2 className="text-base font-semibold text-foreground mr-2">Emails{data ? ` (${data.total})` : ''}</h2>
          <SearchInput value={q} onChange={(value) => setParams({ q: value, page: null }, { replace: true })} placeholder="Search emails..." />
        </Toolbar>
        {!data ? <Spinner /> : (
          <>
            <DataTable
              label="Emails"
              columns={columns}
              rows={data.emails}
              selectedId={selectedId}
              onRowClick={(e) => setParams({ email: e._id })}
              emptyMessage={q ? 'No emails match your search.' : 'No emails archived yet.'}
            />
            <Pagination page={data.page} pages={data.pages} total={data.total} limit={data.limit} onChange={(n) => setParams({ page: n === 1 ? null : n })} />
          </>
        )}
      </Panel>
    </ListLayout>
  );
}

export default EmailsTab;
